import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../api/api';

const Alergias = () => {
  const [alergias, setAlergias] = useState([]);
  const [pets, setPets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedAlergia, setSelectedAlergia] = useState(null);
  const [formData, setFormData] = useState({
    id_pet: '',
    descripcion: '',
    habilitada: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [userIdPet, setUserIdPet] = useState(null);
  const [userIdRol, setUserIdRol] = useState(null);
  const [userSucursales, setUserSucursales] = useState([]);

  // Normalizar datos de alergia
  const normalizeAlergia = (alergia) => {
    if (!alergia || !alergia.id_alergia) {
      console.warn('Alergia inválida:', alergia);
      return null;
    }
    const habilitadaValue = typeof alergia.habilitada === 'boolean'
      ? alergia.habilitada
      : typeof alergia.habilitada === 'number'
        ? alergia.habilitada === 1
        : alergia.habilitada === 'true';
    console.log('Normalizando alergia:', {
      id_alergia: alergia.id_alergia,
      habilitadaRaw: alergia.habilitada,
      habilitadaNormalized: habilitadaValue,
      typeRaw: typeof alergia.habilitada,
    });
    return {
      id_alergia: alergia.id_alergia,
      id_pet: alergia.id_pet,
      descripcion: alergia.descripcion || '',
      habilitada: habilitadaValue,
      createdAt: alergia.createdAt,
      updatedAt: alergia.updatedAt,
    };
  };

  // Cargar datos
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No se encontró un token de usuario');
        setSnackbarMessage('No se encontró un token de usuario');
        setSnackbarOpen(true);
        setLoading(false);
        return;
      }

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserIdPet(payload.id_pet || null);
        setUserIdRol(payload.id_rol || null);
        setUserSucursales(payload.sucursales || []);

        if (!payload.id_pet || !payload.id_rol) {
          throw new Error('ID de usuario o rol no disponible');
        }

        setLoading(true);
        const alergiasRes = await api.get('/alergias/');
        console.log('Datos crudos de GET /alergias:', alergiasRes.data);
        const normalizedAlergias = alergiasRes.data
          .filter(item => item && item.id_alergia)
          .map(normalizeAlergia)
          .filter(alergia => alergia && [payload.id_pet, ...(payload.sucursales || [])].includes(alergia.id_pet));
        console.log('Alergias normalizadas:', normalizedAlergias);
        setAlergias(normalizedAlergias);

        const petsRes = await api.get('/pets/');
        setPets(petsRes.data.filter(pet => [payload.id_pet, ...(payload.sucursales || [])].includes(pet.id_pet)));
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError('Error al cargar datos: ' + (error.response?.data?.detail || error.message));
        setSnackbarMessage('Error al cargar datos');
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtrar alergias por búsqueda
  const filteredAlergias = alergias.filter(
    (alergia) =>
      alergia.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (pets.find(p => p.id_pet === alergia.id_pet)?.nombre_veterinaria || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );
  const paginatedAlergias = filteredAlergias.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Toggle habilitada
  const handleToggleHabilitada = async (id) => {
    const alergia = alergias.find(a => a.id_alergia === id);
    if (!alergia) {
      setError('Alergia no encontrada');
      setSnackbarMessage('Alergia no encontrada');
      setSnackbarOpen(true);
      return;
    }

    try {
      console.log('Toggle para id_alergia:', id, 'habilitada:', alergia.habilitada, typeof alergia.habilitada);
      const payload = {
        id_pet: alergia.id_pet,
        descripcion: alergia.descripcion,
        habilitada: !alergia.habilitada,
      };
      const response = await api.put(`/alergias/${id}`, payload);
      console.log('Respuesta de PUT:', response.data);
      const updatedAlergia = normalizeAlergia(response.data);
      if (updatedAlergia) {
        const newAlergias = alergias.map(a => (a.id_alergia === id ? updatedAlergia : a));
        setAlergias(newAlergias);
        setSnackbarMessage(`Alergia ${updatedAlergia.habilitada ? 'habilitada' : 'deshabilitada'} exitosamente`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error al actualizar alergia:', error);
      setError('Error al actualizar alergia: ' + (error.response?.data?.detail || error.message));
      setSnackbarMessage('Error al actualizar alergia');
      setSnackbarOpen(true);
    }
  };

  // Abrir modal
  const handleOpenModal = (alergia = null) => {
    setSelectedAlergia(alergia);
    setFormData({
      id_pet: alergia ? alergia.id_pet : '',
      descripcion: alergia ? alergia.descripcion : '',
      habilitada: alergia ? alergia.habilitada : true,
    });
    setOpenModal(true);
  };

  // Guardar alergia
  const handleSave = async () => {
    try {
      const payload = {
        id_pet: parseInt(formData.id_pet),
        descripcion: formData.descripcion,
        habilitada: formData.habilitada,
      };
      const response = selectedAlergia
        ? await api.put(`/alergias/${selectedAlergia.id_alergia}`, payload)
        : await api.post('/alergias/', payload);
      console.log('Respuesta de guardado:', response.data);
      const updatedAlergia = normalizeAlergia(response.data);
      if (updatedAlergia) {
        setAlergias(
          selectedAlergia
            ? alergias.map(a => (a.id_alergia === updatedAlergia.id_alergia ? updatedAlergia : a))
            : [...alergias, updatedAlergia]
        );
        setSnackbarMessage(`Alergia ${selectedAlergia ? 'actualizada' : 'creada'} exitosamente`);
        setSnackbarOpen(true);
        setOpenModal(false);
      }
    } catch (error) {
      console.error('Error al guardar alergia:', error);
      setError('Error al guardar alergia: ' + (error.response?.data?.detail || error.message));
      setSnackbarMessage('Error al guardar alergia');
      setSnackbarOpen(true);
    }
  };

  // Eliminar alergia
  const handleDelete = async (id) => {
    try {
      await api.delete(`/alergias/${id}`);
      setAlergias(alergias.filter(a => a.id_alergia !== id));
      setSnackbarMessage('Alergia eliminada exitosamente');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error al eliminar alergia:', error);
      setError('Error al eliminar alergia: ' + (error.response?.data?.detail || error.message));
      setSnackbarMessage('Error al eliminar alergia');
      setSnackbarOpen(true);
    }
  };

  // Formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Alergias
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar alergias..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>
      <Container maxWidth={false}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : alergias.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin datos</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ mt: 2 }}
            >
              Nueva Alergia
            </Button>
          </Box>
        ) : (
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ mb: 2 }}
            >
              Nueva Alergia
            </Button>
            <TableContainer component={Paper}>
              <Table aria-label="tabla de alergias">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Veterinaria</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Habilitada</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Creado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actualizado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAlergias.map((row) => {
                    if (!row || !row.id_alergia) return null;
                    console.log('Renderizando alergia:', {
                      id: row.id_alergia,
                      descripcion: row.descripcion,
                      habilitada: row.habilitada,
                      type: typeof row.habilitada,
                    });
                    return (
                      <TableRow key={row.id_alergia}>
                        <TableCell align="center">{row.id_alergia}</TableCell>
                        <TableCell align="center">{row.descripcion}</TableCell>
                        <TableCell align="center">
                          {pets.find(p => p.id_pet === row.id_pet)?.nombre_veterinaria || 'N/A'}
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={row.habilitada}
                            onChange={() => handleToggleHabilitada(row.id_alergia)}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="center">{formatDate(row.createdAt)}</TableCell>
                        <TableCell align="center">{formatDate(row.updatedAt)}</TableCell>
                        <TableCell align="center">
                          <IconButton onClick={() => handleOpenModal(row)} color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(row.id_alergia)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <Typography sx={{ mx: 2, alignSelf: 'center' }}>
                Página {page + 1} de {Math.ceil(filteredAlergias.length / rowsPerPage)}
              </Typography>
              <Button
                variant="contained"
                disabled={page >= Math.ceil(filteredAlergias.length / rowsPerPage) - 1}
                onClick={() => setPage(page + 1)}
              >
                Siguiente
              </Button>
            </Box>
          </Box>
        )}
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogTitle>{selectedAlergia ? 'Editar Alergia' : 'Nueva Alergia'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Descripción"
              fullWidth
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              select
              label="Veterinaria"
              fullWidth
              value={formData.id_pet}
              onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
              sx={{ mt: 2 }}
              required
            >
              {pets.map((pet) => (
                <MenuItem key={pet.id_pet} value={pet.id_pet}>
                  {pet.nombre_veterinaria}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={formData.habilitada}
                onChange={(e) => setFormData({ ...formData, habilitada: e.target.checked })}
              />
              <Typography>Habilitada</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.descripcion || !formData.id_pet}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert severity={error ? 'error' : 'success'} onClose={() => setSnackbarOpen(false)}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Alergias;